import { ChevronUp, ChevronDown, Trash2, Copy, RotateCcw, Layers as LayersIcon } from 'lucide-react';
import { tintFilter } from '../lib/tints';

export const LayersPanel = ({
  layers,
  selectedId,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDelete,
  onDuplicate,
  onResetTransform,
}) => {
  // Display layers from top (highest z) to bottom
  const sorted = [...layers].sort((a, b) => b.zIndex - a.zIndex);

  return (
    <aside
      data-testid="layers-panel"
      className="w-full lg:w-72 flex-shrink-0 border-l-2 border-ink bg-paper overflow-y-auto tidy-scroll"
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="label-tiny">Layers</h4>
          <span data-testid="layers-count" className="font-mono text-xs text-ink/60">{layers.length}</span>
        </div>

        {sorted.length === 0 ? (
          <div className="border-2 border-dashed border-ink/40 rounded-sm p-8 text-center">
            <div className="h-12 w-12 mx-auto border-2 border-ink/40 rounded-sm flex items-center justify-center">
              <LayersIcon className="h-5 w-5 text-ink/40" />
            </div>
            <p className="font-work text-sm mt-3 text-ink/60">No layers yet.<br/>Add an image to begin.</p>
          </div>
        ) : (
          <ul data-testid="layers-list" className="space-y-2">
            {sorted.map((layer) => {
              const active = layer.id === selectedId;
              return (
                <li
                  key={layer.id}
                  data-testid={`layer-item-${layer.id}`}
                  onClick={() => onSelect(layer.id)}
                  className={`flex items-center gap-2 p-2 border-2 border-ink rounded-sm cursor-pointer transition-all ${active ? 'bg-lilac shadow-brutSm -translate-y-0.5' : 'bg-paper hover:bg-canvasbg'}`}
                >
                  <div className="h-10 w-10 flex-shrink-0 border-2 border-ink bg-canvasbg overflow-hidden checker-bg">
                    <img src={layer.src} alt="" className="h-full w-full object-contain" style={{ filter: tintFilter(layer.tint) }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-outfit font-bold text-xs truncate">Layer {layer.zIndex + 1}</p>
                    <p className="font-mono text-[10px] text-ink/60">
                      {Math.round(layer.width)}×{Math.round(layer.height)} · {Math.round(layer.rotation)}°
                    </p>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <button
                      data-testid={`layer-up-${layer.id}`}
                      aria-label="Bring forward"
                      onClick={(e) => { e.stopPropagation(); onMoveUp(layer.id); }}
                      className="h-5 w-5 border border-ink bg-paper hover:bg-mint flex items-center justify-center"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      data-testid={`layer-down-${layer.id}`}
                      aria-label="Send backward"
                      onClick={(e) => { e.stopPropagation(); onMoveDown(layer.id); }}
                      className="h-5 w-5 border border-ink bg-paper hover:bg-mint flex items-center justify-center"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {selectedId && (
          <div data-testid="layer-actions" className="mt-4 grid grid-cols-3 gap-2">
            <button
              data-testid="layer-reset-btn"
              onClick={() => onResetTransform(selectedId)}
              className="neo-btn bg-paper h-10 text-xs"
              aria-label="Reset transform"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="layer-duplicate-btn"
              onClick={() => onDuplicate(selectedId)}
              className="neo-btn bg-mint h-10 text-xs"
              aria-label="Duplicate"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="layer-delete-btn"
              onClick={() => onDelete(selectedId)}
              className="neo-btn bg-rose h-10 text-xs"
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
